/**
 * IntInput — atlas Input for bare integer counts (e.g. Utilized Loss
 * Years). Digits only. Mirrors PercentInput's controlled contract —
 * including the synchronous local buffer so typing paints instantly
 * even when the parent's re-render is deferred.
 */
import { useEffect, useRef, useState } from "react";
import { Input } from "@tricura/atlas-ui";

interface IntInputProps {
    /** Raw integer string, e.g. "5". */
    value: string;
    onChange?: (raw: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    isDisabled?: boolean;
    isInvalid?: boolean;
    size?: "sm" | "md" | "lg";
    "aria-label"?: string;
    /** Extra class on the outer wrapper (e.g. width caps). */
    className?: string;
}

export function IntInput({
    value,
    onChange,
    onBlur,
    placeholder = "0",
    isDisabled,
    isInvalid,
    size = "sm",
    "aria-label": ariaLabel,
    className,
}: IntInputProps) {
    const lastEmitted = useRef(value);
    const [local, setLocal] = useState(value);
    useEffect(() => {
        if (value !== lastEmitted.current) {
            lastEmitted.current = value;
            setLocal(value);
        }
    }, [value]);

    const handleChange = (raw: string) => {
        const next = raw.replace(/\D/g, "");
        lastEmitted.current = next;
        setLocal(next);
        onChange?.(next);
    };

    return (
        <div className={`relative w-full ${className ?? ""}`}>
            <Input
                aria-label={ariaLabel}
                size={size}
                placeholder={placeholder}
                value={local}
                onChange={handleChange}
                onBlur={onBlur}
                isDisabled={isDisabled}
                isInvalid={isInvalid}
                inputMode="numeric"
                autoComplete="off"
                inputClassName="text-right"
            />
        </div>
    );
}
